import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { setSession } from '@zipdrug/react-api-sdk';
import { WorkstationPhoneContext } from 'contexts/WorkstationPhoneContextProvider';
import { CallCenterContext } from 'contexts/CallCenterContextProvider';
import LoadingPage from '../ui/LoadingPage';
import { SessionContext, ActionTypes } from '../contexts/SessionContextProvider';
import { queryCurrentUser } from '../graphql/users';
import { queryPhones } from '../graphql/phones';
import fetchOnceOptions from '../hoc/utils/fetchOnceOptions';
import { IS_NEW_BROWSER_SESSION } from './AuthenticatedComponent';

const AuthenticatedRoute = ({ element, allowedRoles, allowedFeatureFlags, ...rest }) => {
  const location = useLocation();
  const { userId, state, dispatch, ready } = useContext(SessionContext);
  const { workstationPhoneId } = useContext(WorkstationPhoneContext);
  const { updateCallCenterUserStatusOfflineMutationContext } = useContext(CallCenterContext);
  const { currentUser } = state;
  const hasRole = get(currentUser, 'hasRole');
  const reduxDispatch = useDispatch();
  const [tokenRetrievalComplete, setTokenRetrievalComplete] = useState(false);
  const tokenRetrievalCompleteRef = useRef(false);

  const authSessionCookie = Cookies.get('auth-session-cookie');
  const authSessionRefreshCookie = Cookies.get('session-refresh-cookie');
  const newBrowserSessionCookie = Cookies.get(IS_NEW_BROWSER_SESSION);

  // For direct Okta entry, treat either auth cookie as valid auth signal.
  const hasAuthCookies = !!(authSessionCookie || authSessionRefreshCookie);
  const shouldSkipQuery = !userId && !hasAuthCookies;

  // Call /token-retrieval for OKTA users who have cookies but no Redux session
  useEffect(() => {
    const retrieveTokens = async () => {
      // Only call if we have cookies but no userId in Redux
      if (hasAuthCookies && !userId && !tokenRetrievalComplete) {
        try {
          const root = window.location.origin.replace('care', 'api');
          const tokenUrl = `${root}/token-retrieval`;
          
          const response = await fetch(tokenUrl, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-cache',
          });

          if (response.status === 200) {
            const tokenData = await response.json();
            
            if (tokenData?.data) {
              const { user, accessToken, refreshToken } = tokenData.data;
              
              reduxDispatch(
                setSession({
                  token: accessToken,
                  refreshToken,
                  user,
                })
              );
              
              tokenRetrievalCompleteRef.current = true;
              setTokenRetrievalComplete(true);
            }
          }
        } catch (error) {
          console.error('[AuthenticatedRoute] Failed to retrieve tokens:', error);
        }
      }
    };

    retrieveTokens();
  }, [hasAuthCookies, userId, tokenRetrievalComplete, reduxDispatch]);

  // Keep currentUser deterministic across regular/incognito windows.
  const forceNetworkFetch = true;

  

  const setOfflineStatusForOktaUser = user => {
    if (!userId && user?.feature_flags?.callCenter && updateCallCenterUserStatusOfflineMutationContext) {
      const sessionKey = `callCenterOfflineInitialized:${user.id}`;
      if (sessionStorage.getItem(sessionKey) === 'true') {
        return;
      }

      updateCallCenterUserStatusOfflineMutationContext()
        .then(() => {
          sessionStorage.setItem(sessionKey, 'true');
        })
        .catch(error => {
          console.error(
            `[AuthenticatedRoute] Failed to set call center user status to offline for user ${user.id}:`,
            error,
          );
        });
    }
  };

  const { loading: userLoading, data: userData } = useQuery(queryCurrentUser, {
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError(e) {
      console.error('[AuthenticatedRoute] currentUser query error:', e);
      if (e) {
        dispatch({
          type: ActionTypes.ERROR,
          payload: e,
        });
      }
    },
    onCompleted(data) {
      if (data) {
        dispatch({
          type: ActionTypes.SET_CURRENT_USER,
          payload: data,
        });

        setOfflineStatusForOktaUser(data?.currentUser?.data);

        // For Okta/cookie-authenticated users, sync user to Redux session store
        // so that withSession HOC and all downstream queries (withUsers, withCurrentUser)
        // receive a valid userId and don't skip their GraphQL queries.
        if ((!userId && data?.currentUser?.data) || userId !== data?.currentUser?.data?.id) {
          // If /token-retrieval already set a valid JWT, preserve it.
          // Falling back to auth-session-cookie would overwrite the JWT with a
          // non-Bearer-compatible cookie value, causing all subsequent API calls to fail.
          if (!tokenRetrievalCompleteRef.current) {
            // Cookie-based fallback for non-Okta or pre-retrieval path.
            const authToken = Cookies.get('auth-session-cookie') || null;
            const refreshToken = Cookies.get('session-refresh-cookie') || null;
            reduxDispatch(
              setSession({
                token: authToken,
                refreshToken: refreshToken,
                user: data.currentUser.data,
              }),
            );
          }
        }
      }
    },
  });

  const { loading: phonesLoading } = useQuery(queryPhones, {
    ...fetchOnceOptions('phones', {
      skip: !userId || !userData || !workstationPhoneId,
      variables: {
        query: {
          is_zipdrug: !hasRole?.pharmacistUser || !hasRole?.pharmacistAdmin,
          id: workstationPhoneId,
        },
      },
    }),
    notifyOnNetworkStatusChange: true,
  });

  const loading = phonesLoading || userLoading;

  const hasAllowedRole = () => {
    if (currentUser) {
      const userRoles = currentUser?.roles?.map(role => role?.type).filter(Boolean) || [];
      const userFlags = currentUser?.feature_flags;
      const isAllowed =
        (allowedRoles ? allowedRoles.some(allowedRole => userRoles.includes(allowedRole)) : true) &&
        (allowedFeatureFlags
          ? Object.keys(allowedFeatureFlags).every(flag =>
              allowedFeatureFlags[flag]
                ? allowedFeatureFlags[flag] === userFlags?.[flag]
                : userFlags?.[flag] !== true,
            )
          : true);
      return isAllowed;
    }
    return true;
  };

  if (loading || !ready) {
    console.log('[AuthenticatedRoute] Showing loading page:', { loading, ready });
    return <LoadingPage />;
  }

  // For Okta/SAML users, userId might not be in Redux yet, but userData will be populated
  const effectiveUserId = userId || userData?.currentUser?.data?.id;
  
  // For direct Okta flow, authenticate by user id or auth cookies.
  const isUserAuthenticated = !!(effectiveUserId || hasAuthCookies);

  if (!isUserAuthenticated) {
    console.warn('[AuthenticatedRoute] Authentication failed, redirecting to /signin');
    return <Navigate to="/signin" />;
  }

  console.log('[AuthenticatedRoute] Authentication passed, rendering route');

  if (!hasAllowedRole()) {
    return <Navigate to="/members" />;
  }

  return React.cloneElement(element, { ...rest });
};

AuthenticatedRoute.propTypes = {
  element: PropTypes.element.isRequired,
  allowedRoles: PropTypes.array,
  allowedFeatureFlags: PropTypes.object,
};

export default AuthenticatedRoute;
